import { PartialLabelData, SchedulerConfigurationData } from '../api';

/**
 * The single place resource masks are composed and parsed.
 *
 * A mask is a binary string of length `resource_count`. The character at string position
 * `resource_count - 1 - i` corresponds to resource index `i` — the RIGHTMOST character is
 * resource 0. Everything else in the areas view reads occupancy numbers from the server and
 * never derives them from masks.
 */

const isBinary = (mask: string) => /^[01]*$/.test(mask);

export const maskToIndexes = (mask: string): number[] => {
  if (!mask || !isBinary(mask)) {
    return [];
  }
  const indexes: number[] = [];
  for (let position = 0; position < mask.length; position++) {
    if (mask[position] === '1') {
      indexes.push(mask.length - 1 - position);
    }
  }
  return indexes.sort((a, b) => a - b);
};

export const indexesToMask = (indexes: number[], resourceCount: number): string => {
  const characters = new Array(resourceCount).fill('0');
  indexes.forEach((index) => {
    if (index >= 0 && index < resourceCount) {
      characters[resourceCount - 1 - index] = '1';
    }
  });
  return characters.join('');
};

export const popcount = (mask: string): number => {
  if (!mask || !isBinary(mask)) {
    return 0;
  }
  let count = 0;
  for (const character of mask) {
    if (character === '1') {
      count++;
    }
  }
  return count;
};

const combine = (a: string, b: string, operation: (x: boolean, y: boolean) => boolean): string => {
  const length = Math.max(a.length, b.length);
  const paddedA = a.padStart(length, '0');
  const paddedB = b.padStart(length, '0');
  let result = '';
  for (let position = 0; position < length; position++) {
    result += operation(paddedA[position] === '1', paddedB[position] === '1') ? '1' : '0';
  }
  return result;
};

export const intersectMasks = (a: string, b: string): string => combine(a, b, (x, y) => x && y);

export const unionMasks = (a: string, b: string): string => combine(a, b, (x, y) => x || y);

export const subtractMask = (a: string, b: string): string => combine(a, b, (x, y) => x && !y);

export const maskHasIndex = (mask: string, index: number): boolean => {
  const position = mask.length - 1 - index;
  return position >= 0 && position < mask.length && mask[position] === '1';
};

export const masksIntersect = (a: string, b: string): boolean => popcount(intersectMasks(a, b)) > 0;

/** True when `blockedMask` covers every bit of `areaMask` — i.e. the whole area is blocked. */
export const isWholeAreaBlock = (blockedMask: string, areaMask: string): boolean =>
  popcount(areaMask) > 0 && popcount(subtractMask(areaMask, blockedMask)) === 0;

/**
 * The area a blocked mask belongs to, when every blocked bit sits inside one area. Returns
 * undefined for an empty mask or one spanning several areas, where no single area owns it.
 *
 * Blocks carry `area_id` so the day view can list and lift them per area; a block written without
 * it is invisible there even though it still reduces capacity.
 */
export const areaIdForMask = (mask: string, areas: { id: string; mask: string }[]): string | undefined => {
  const blocked = maskToIndexes(mask);
  if (blocked.length === 0) {
    return undefined;
  }
  const containing = areas.filter((area) => {
    const areaIndexes = new Set(maskToIndexes(area.mask));
    return blocked.every((index) => areaIndexes.has(index));
  });
  return containing.length === 1 ? containing[0].id : undefined;
};

const labelText = (label: PartialLabelData | undefined, language: string): string | undefined => {
  if (!label) {
    return undefined;
  }
  const value = language === 'he' ? label.he || label.en : label.en || label.he;
  return value || undefined;
};

/**
 * Display name for a single resource: its configured label when `resource_labels` is present,
 * otherwise a 1-BASED number (index 0 shows as "1").
 */
export const resourceDisplayName = (
  index: number,
  config: SchedulerConfigurationData | undefined,
  language: string,
): string => {
  const label = labelText(config?.resource_labels?.[index], language);
  return label || String(index + 1);
};

const numberedLabelNumber = (name: string): string | undefined => name.match(/^\D*\s(\d+)$/)?.[1];

/**
 * Compact name for a resource in dense pickers: a "word + number" label ("Table 4", "שולחן 4") shows as
 * the number alone. Any other label ("Tables 1-2", "Window") shows as is, and so does a number another
 * resource in the config would also show ("Bar 3" next to "Table 3"), so two buttons never read the same.
 */
export const resourceShortName = (
  index: number,
  config: SchedulerConfigurationData | undefined,
  language: string,
): string => {
  const name = resourceDisplayName(index, config, language);
  const number = numberedLabelNumber(name);
  if (!number) {
    return name;
  }
  const resourceCount = Math.max(config?.resource_count ?? 0, config?.resource_labels?.length ?? 0);
  const isAmbiguous = Array.from({ length: resourceCount }).some((_, other) => {
    if (other === index) return false;
    const otherName = resourceDisplayName(other, config, language);
    return (numberedLabelNumber(otherName) ?? otherName) === number;
  });
  return isAmbiguous ? name : number;
};

export const localizedLabel = (label: PartialLabelData | undefined, language: string, fallback = ''): string =>
  labelText(label, language) || fallback;
